import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Observable } from "rxjs";
import { environment } from "../../environments/environment";
import { API_ENDPOINTS, ROUTE_PREFIX, USER_TYPE } from "../constants";
import { StorageService } from "./storage.service";
import {PublicHttpService} from "./publicHttp.service";
import {ProfileService} from "./profile.service";
import {SessionService} from "./session.service";
import {tap} from "rxjs/operators";

export interface ILoginContext {
	email: string;
	password: string;
}

export interface IRegisterContext {
	organizationName?: string;
	email: string;
	password: string;
	preferredLanguage: string;
	firstName?: string;
	lastName?: string;
}

export interface ILogin {
	token: string;
}

export interface IForgotPasswordContext {
	email: string;
}

export interface IResetPasswordContext {
	email: string;
	password: string;
	code: string;
	confirmPassword: string;
}

export interface IActivation {
	email: string;
	code: string;
}

export interface IActivationLink {
	email: string;
}

@Injectable( {
	providedIn: "root"
} )
export class AuthService {
	private httpClient: HttpClient;

	constructor(
		private route: Router,
		private storageService: StorageService,
		private publicHttpService: PublicHttpService,
		private sessionService: SessionService,
		private profileService: ProfileService
	) {
		this.httpClient = publicHttpService.publicHttpClient;
	}

	/**
	 *	Register function
	 * @param data : data object to register with
	 */
	register( data: IRegisterContext, organizationType: string ): Observable<object> {
		return this.httpClient.post<IRegisterContext>( environment.API_ENDPOINT + ROUTE_PREFIX.PUBLIC + this.getRouterPrefix( organizationType ) + API_ENDPOINTS.REGISTER, data );
	}

	/**
	 * Verify link by code
	 */
	verifyLinkCode( code: string, organizationType: string ) {
		const url =  environment.API_ENDPOINT + ROUTE_PREFIX.PUBLIC + this.getRouterPrefix( organizationType ) + API_ENDPOINTS.VERIFY_LINK + `?eventLinkCode=${code}`;
		return this.httpClient.get(url);
	}

	/**
	 *	Login function
	 * @param data : data object for login
	 */
	login( data: ILoginContext, userType: string ): Observable<any> {
		return this.httpClient.post<ILoginContext>( environment.API_ENDPOINT + ROUTE_PREFIX.PUBLIC + this.getRouterPrefix( userType ) + API_ENDPOINTS.LOGIN, data, { withCredentials: true } );
	}

	refreshToken(): Observable<any> {
		let URL = environment.API_ENDPOINT + ROUTE_PREFIX.PUBLIC + this.getRouterPrefix(this.sessionService.userType)
			+ API_ENDPOINTS.REFRESH_TOKEN + '/' + this.sessionService.userId;

		return this.httpClient.get<any>( URL, { withCredentials: true }).pipe(
			tap(response => {
				this.sessionService.idToken = response.idToken;
			}, error => {
				const errorPayload = {
					error: {
						message: error.message,
						statusCode: error.status,
						error: error.error,
						details: this.sessionService.userId
					}
				};
				if (typeof error.error !== 'string') {
					errorPayload.error.error = JSON.stringify(error.error);
				}
				this.profileService.logoutProfile();
			})
		);
	}

	/**
	 *	Login function
	 * @param token : string object for login
	 */
	loginAsAdmin( token: string): Observable<any> {
		return this.httpClient.post<{authToken: string}>( environment.API_ENDPOINT + ROUTE_PREFIX.PUBLIC + ROUTE_PREFIX.ADMIN + API_ENDPOINTS.LOGIN_ADMIN, { authToken: token }, { withCredentials: true } );
	}

	/**
	 *	Logout functionality
	 */
	logout(userType: string ): Observable<object> {
		return this.httpClient.get( environment.API_ENDPOINT + ROUTE_PREFIX.PUBLIC + this.getRouterPrefix( userType ) + API_ENDPOINTS.LOGOUT, {withCredentials: true} );
	}

	/**
	 *	Request for reset password link via email
	 */
	forgotPassword( data: IForgotPasswordContext, userType: string ): Observable<object> {
		return this.httpClient.post<IForgotPasswordContext>( environment.API_ENDPOINT + ROUTE_PREFIX.PUBLIC + this.getRouterPrefix( userType ) + API_ENDPOINTS.FORGOT_PASSWORD, data );
	}

	/**
	 *	Reset your current password
	 */
	resetPassword( data: IResetPasswordContext, userType: string ): Observable<object> {
		return this.httpClient.put<IResetPasswordContext>( environment.API_ENDPOINT + ROUTE_PREFIX.PUBLIC + this.getRouterPrefix( userType ) + API_ENDPOINTS.RESET_PASSWORD, data );
	}

	/**
	 *	Activate your account
	 */
	activation( data: IActivation, userType: string ): Observable<object> {
		return this.httpClient.put<IActivation>( environment.API_ENDPOINT + ROUTE_PREFIX.PUBLIC + this.getRouterPrefix( userType ) + API_ENDPOINTS.ACTIVATE, data );
	}

	/**
	 *	Activate your account
	 */
	sendActivationLink( data: IActivationLink, userType: string ): Observable<object> {
		return this.httpClient.post<IActivationLink>( environment.API_ENDPOINT + ROUTE_PREFIX.PUBLIC + this.getRouterPrefix( userType ) + API_ENDPOINTS.ACTIVATION_LINK, data );
	}

	/**
	 * Get route prefix for user type
	 * @param userType
	 */
	getRouterPrefix( userType: string ): string {
		return userType === USER_TYPE.BUYER ? ROUTE_PREFIX.BUYER : ROUTE_PREFIX.SUPPLIER;
	}

	invitationForNewUser( userData:any ): Observable<object> {
		return this.httpClient.put( environment.API_ENDPOINT + ROUTE_PREFIX.PUBLIC +
			 environment.API_ROUTES.BUYER.ORGANISATION.INVITE_ACCEPT , userData );
	}

	invitationForSupplierUser( supplierData: any ): Observable<object> {
		return this.httpClient.put( environment.API_ENDPOINT + ROUTE_PREFIX.PUBLIC + environment.API_ROUTES.SUPPLIER.ORGANISATION.INVITE_ACCEPT , supplierData );
	}

	checkValidationCodeIsValid(code: string, email: string, usertype: string): Observable<boolean> {
		return new Observable<boolean>(observer => {
			let URL: string = environment.API_ENDPOINT + ROUTE_PREFIX.PUBLIC + this.getRouterPrefix(usertype) + API_ENDPOINTS.VERIFY_VALIDATION;
			const body = {
				email: email,
				code: code
			};
			this.httpClient.post(URL, body).subscribe(ok => {
				observer.next(true);
			}, error => {
				observer.next(false);
			});
		});
	}

}
